/**
 * Spend Analytics API client — functions for all spend analytics endpoints.
 *
 * Requirements: 46.2, 46.3, 46.6, 46.9, 46.10, 46.11, 46.13
 */

import { apiClient } from '@/services/apiClient';

// ─── Spend Trending Types ──────────────────────────────────────────────

export type TrendDirection = 'up' | 'down' | 'flat';

export interface SpendTrendingParams {
  granularity?: 'monthly' | 'quarterly' | 'yearly';
  dateFrom?: string;
  dateTo?: string;
}

export interface SpendTrendItem {
  period: string;
  amount: number;
  previousAmount: number;
  changePercent: number;
  direction: TrendDirection;
}

// ─── Vendor Spend Types ────────────────────────────────────────────────

export interface VendorSpendParams {
  vendorId?: number;
  dateFrom?: string;
  dateTo?: string;
}

export interface VendorSpendItem {
  vendorId: number;
  vendorName: string;
  contractedAmount: number;
  invoicedAmount: number;
  variance: number;
  variancePercent: number;
}

// ─── Category Breakdown Types ──────────────────────────────────────────

export interface CategoryBreakdownParams {
  dateFrom?: string;
  dateTo?: string;
}

export interface SpendCategoryItem {
  category: string;
  amount: number;
  percentOfTotal: number;
}

// ─── Spend Forecast Types ──────────────────────────────────────────────

export interface SpendForecastParams {
  fiscalYear?: number;
}

export interface SpendForecastResult {
  projectedAnnual: number;
  ytdActual: number;
  remainingProjected: number;
  trend: TrendDirection;
}

// ─── Anomaly Types ─────────────────────────────────────────────────────

export interface AnomalyListParams {
  anomalyType?: string;
  vendorId?: number;
  severity?: string;
  isResolved?: boolean;
  page?: number;
  pageSize?: number;
}

export interface SpendAnomaly {
  anomalyId: number;
  anomalyType: string;
  vendorId: number | null;
  affectedPeriod: string | null;
  expectedAmount: number | null;
  actualAmount: number | null;
  deviationPercentage: number | null;
  recommendedAction: string | null;
  severity: string | null;
  isResolved: boolean;
  resolutionNotes: string | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
  createdAt: string;
}

export interface AnomalyListResult {
  data: SpendAnomaly[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// ─── Cost Optimization Types ───────────────────────────────────────────

export interface CostOptimizationSuggestion {
  type: 'yoy_increase' | 'over_budget' | 'contract_overage';
  description: string;
  supportingData: Record<string, unknown>;
  severity: 'High' | 'Medium' | 'Low';
}

export interface CostOptimizationResult {
  suggestions: CostOptimizationSuggestion[];
  disclaimer: string;
}

// ─── Service ───────────────────────────────────────────────────────────

export const spendAnalyticsService = {
  /** Fetch spend trending data. */
  getSpendTrending(params: SpendTrendingParams = {}): Promise<SpendTrendItem[]> {
    return apiClient.get('analytics/spend-trending', params as Record<string, string | number | boolean | undefined>);
  },

  /** Get CSV export URL for spend trending. */
  getSpendTrendingExportUrl(params: SpendTrendingParams = {}): string {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') {
        searchParams.set(key, String(value));
      }
    }
    const qs = searchParams.toString();
    return `/api/analytics/spend-trending/export${qs ? `?${qs}` : ''}`;
  },

  /** Fetch vendor spend analysis. */
  getVendorSpend(params: VendorSpendParams = {}): Promise<VendorSpendItem[]> {
    return apiClient.get('analytics/vendor-spend', params as Record<string, string | number | boolean | undefined>);
  },

  /** Fetch spend by category breakdown. */
  getCategoryBreakdown(params: CategoryBreakdownParams = {}): Promise<SpendCategoryItem[]> {
    return apiClient.get('analytics/category-breakdown', params as Record<string, string | number | boolean | undefined>);
  },

  /** Fetch spend forecast. */
  getSpendForecast(params: SpendForecastParams = {}): Promise<SpendForecastResult> {
    return apiClient.get('analytics/spend-forecast', params as Record<string, string | number | boolean | undefined>);
  },

  /** Fetch spend anomalies. */
  getAnomalies(params: AnomalyListParams = {}): Promise<AnomalyListResult> {
    return apiClient.get('analytics/anomalies', params as Record<string, string | number | boolean | undefined>);
  },

  /** Resolve a spend anomaly. */
  resolveAnomaly(anomalyId: number, notes: string): Promise<SpendAnomaly> {
    return apiClient.post(`analytics/anomalies/${anomalyId}/resolve`, { notes });
  },

  /** Fetch cost optimization suggestions. */
  getCostOptimization(): Promise<CostOptimizationResult> {
    return apiClient.get('analytics/cost-optimization');
  },
};
